import React, { useState } from 'react';
import { Brain, Wrench, CheckCircle2, AlertCircle, MessageSquare, ChevronDown, ChevronUp, Clock, Pause } from 'lucide-react';

const STEP_ICONS = {
    thought: { icon: Brain, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Thinking' },
    tool_call: { icon: Wrench, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Tool Call' },
    tool_result: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Result' },
    approval_gate: { icon: Pause, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Approval Required' },
    final_response: { icon: MessageSquare, color: 'text-stone-600', bg: 'bg-stone-50', label: 'Final Response' },
};

function StepCard({ step, isLast }) {
    const [expanded, setExpanded] = useState(false);
    const config = STEP_ICONS[step.type] || STEP_ICONS.thought;
    const Icon = config.icon;

    const hasExpandableContent = step.tool_args || step.tool_result;

    return (
        <div className="flex gap-3">
            {/* Timeline connector */}
            <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                </div>
                {!isLast && <div className="w-0.5 flex-1 bg-stone-200 mt-1" />}
            </div>

            {/* Content */}
            <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
                <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${config.color}`}>{config.label}</span>
                    {step.tool_name && (
                        <code className="text-[10px] font-mono bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded">
                            {step.tool_name}
                        </code>
                    )}
                    {step.dry_run && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                            SANDBOX
                        </span>
                    )}
                    {step.timestamp && (
                        <span className="text-[10px] text-stone-400 flex items-center gap-0.5">
                            <Clock className="w-3 h-3" />
                            {new Date(step.timestamp).toLocaleTimeString()}
                        </span>
                    )}
                </div>

                {/* Main content */}
                {step.content && (
                    <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                        {step.content}
                    </p>
                )}

                {/* Expandable tool details */}
                {hasExpandableContent && (
                    <div className="mt-2">
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="flex items-center gap-1 text-[10px] text-stone-500 hover:text-stone-700 transition-colors"
                        >
                            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            {expanded ? 'Hide details' : 'Show details'}
                        </button>
                        {expanded && (
                            <div className="mt-2 space-y-2">
                                {step.tool_args && (
                                    <div>
                                        <span className="text-[10px] font-semibold text-stone-500 uppercase">Arguments</span>
                                        <pre className="mt-1 p-2 bg-stone-50 border border-stone-200 rounded-lg text-[11px] font-mono text-stone-600 overflow-x-auto">
                                            {JSON.stringify(step.tool_args, null, 2)}
                                        </pre>
                                    </div>
                                )}
                                {step.tool_result && (
                                    <div>
                                        <span className="text-[10px] font-semibold text-stone-500 uppercase">Result</span>
                                        <pre className="mt-1 p-2 bg-stone-50 border border-stone-200 rounded-lg text-[11px] font-mono text-stone-600 overflow-x-auto max-h-48 overflow-y-auto">
                                            {JSON.stringify(step.tool_result, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Displays the multi-step reasoning trace of an agent run.
 * Props:
 *   steps: array of step objects from agent_runs.steps
 *   status: run status string
 */
export default function ReasoningTraceViewer({ steps = [], status = '' }) {
    if (!steps.length) {
        return (
            <div className="flex items-center justify-center h-32 text-sm text-stone-400">
                No execution trace available
            </div>
        );
    }

    return (
        <div className="space-y-0">
            {/* Run status header */}
            {status && (
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-stone-100">
                    <span className="text-xs text-stone-500">Run status:</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        status === 'failed' ? 'bg-red-100 text-red-700' :
                        status === 'running' ? 'bg-blue-100 text-blue-700 animate-pulse' :
                        status === 'paused_for_approval' ? 'bg-amber-100 text-amber-700' :
                        'bg-stone-100 text-stone-600'
                    }`}>
                        {status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-stone-400 ml-auto">{steps.length} steps</span>
                </div>
            )}

            {/* Steps timeline */}
            {steps.map((step, idx) => (
                <StepCard key={idx} step={step} isLast={idx === steps.length - 1} />
            ))}
        </div>
    );
}
